<template>
  <v-container
    fluid
    class="patch-catalog-container">
    <!-- META-DATA -->
    <vue-headful
      :title="'Patch Permanent Catalog' + this.$store.state.app.meta.titleSuffix"
      description="Our catalog of classic patch designs, printed in vibrant color on sturdy vinyl." />

    <div class="patch-catalog-toolbar">
      <div class="patch-catalog-toolbar__header">
        <h2 class="title">Patch Permanent Catalog</h2>
      </div>
      <v-autocomplete
        v-model="selectedTags"
        :items="sortedTags"
        prepend-icon="search"
        multiple
        label="Search Tags"
        class="tag-select">
        <template
          slot="selection"
          slot-scope="{ item, parent }">
          <div
            v-ripple
            class="tag-select__selected-tag"
            @click="parent.selectItem(item)">
            {{ item }}
            <v-icon
              small
              class="tag-select__selected-tag-remove">
              close
            </v-icon>
          </div>
        </template>
      </v-autocomplete>
      <v-select
        v-model="currentSort"
        :items="sortCriteria"
        label="sort by"
        class="sort-select"
        return-object />
    </div>

    <!-- LOADER -->
    <div
      v-if="loading && filteredPatches.length === 0"
      key="loading"
      class="loading-icon">
      <v-progress-circular
        size="100"
        indeterminate
        color="primary" />
    </div>

    <!-- No Results -->
    <v-alert
      outline
      :value="!loading && filteredPatches.length === 0"
      color="accent"
      icon="warning">
      Sorry, we don't have any patches with all the selected tags :(
    </v-alert>

    <!-- PATCH COLLECTION -->
    <v-scroll-y-transition
      group
      mode="out-in"
      class="patch-catalog">
      <router-link
        v-for="patch in paginatedPatches"
        :key="patch.imageId"
        v-ripple
        class="patch-listing"
        :to="{
          name: 'patch-product',
          params: {
            patchGroup: 'catalog',
            id: patch.imageId,
          },
        }">
        <v-img
          :src="imageDir + patch.imageId + '.png'"
          class="patch-listing__image">
          <div
            slot="placeholder"
            class="patch-listing__placeholder">
            <v-progress-circular
              indeterminate
              color="primary" />
          </div>
        </v-img>
      </router-link>
    </v-scroll-y-transition>

    <div class="load-more">
      <span class="load-more__page-details">
        Showing {{ pageItemCount }} of {{ filteredPatches.length }} patches
      </span>
      <v-progress-linear
        v-model="catalogProgress"
        height="2"
        background-color="accent"
        class="load-more__progress" />
      <v-btn
        outline
        color="primary"
        class="load-more__load-more-button"
        :disabled="filteredPatches.length === paginatedPatches.length"
        @click="loadNextPage">
        Load More
      </v-btn>
      <v-btn
        flat
        small
        class="load-more__go-to-top"
        @click="scrollToTop">
        go to top
      </v-btn>
    </div>
  </v-container>
</template>

<script>
import { mapState } from 'vuex';

export default {
  name: 'PatchPermanentCatalog',
  data() {
    return {
      selectedTags: [],
      sortCriteria: [
        {
          text: 'Newest',
          value: 'dateCreated',
          direction: 'desc',
        },
        {
          text: 'Best Selling',
          value: 'quantitySoldRecently',
          direction: 'desc',
        },
      ],
      currentSort: {},
      pageNumber: 1,
      pageLimit: 4,
    };
  },
  computed: {
    ...mapState('patches', {
      tags: 'tags',
      imageDir: 'imageDir',
      loading: 'loading',
      errorMessage: 'errorMessage',
    }),
    patches() {
      return this.$store.state.patches.patches.catalog;
    },
    sortedTags() {
      return this.tags.map(x => x).sort();
    },
    sortedPatches() {
      let dir;
      if (this.currentSort.direction === 'asc') { dir = -1; }
      if (this.currentSort.direction === 'desc') { dir = 1; }

      if (this.currentSort.value === 'dateCreated') {
        return this.patches.map(x => x).sort((a, b) => (new Date(b.dateCreated) - new Date(a.dateCreated)) * dir);
      }
      const sortVal = this.currentSort.value;
      return this.patches.map(x => x).sort((a, b) => {
        if (a[sortVal] < b[sortVal]) return 1 * dir;
        if (a[sortVal] > b[sortVal]) return -1 * dir;
        return 0;
      });
    },
    filteredPatches() {
      return this.sortedPatches.filter((patch) => {
        // tags
        if (this.selectedTags.length !== 0) {
          return this.selectedTags.every(tag => patch.tags.includes(tag));
        }
        return true;
      });
    },
    paginatedPatches() {
      return this.filteredPatches.filter((row, index) => {
        const limit = this.pageNumber * this.pageLimit;
        if (index < limit) return true;
        return false;
      });
    },
    catalogProgress() {
      return (
        ((this.pageNumber * this.pageLimit) / this.filteredPatches.length) * 100
      );
    },
    pageItemCount() {
      return this.pageNumber * this.pageLimit >= this.filteredPatches.length
        ? this.filteredPatches.length
        : this.pageNumber * this.pageLimit;
    },
  },
  created() {
    this.setInitPageLimit();
    this.$store.dispatch('patches/getAllPatches', 'catalog');
    this.$store.dispatch('patches/getTags');

    if (this.$route.params.tag) {
      this.selectedTags.push(this.$route.params.tag);
    }
  },
  methods: {
    setInitPageLimit() {
      const pageWidth = window.innerWidth || document.body.clientWidth;
      if (pageWidth <= 768) {
        this.pageLimit = 12;
      } else {
        this.pageLimit = 36;
      }
    },
    loadNextPage() {
      this.pageNumber++;
    },
    scrollToTop() {
      // https://www.w3schools.com/howto/howto_js_scroll_to_top.asp
      document.body.scrollTop = 0; // For Safari
      document.documentElement.scrollTop = 0; // For Chrome, Firefox, IE and Opera
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.patch-catalog-toolbar
  display: flex
  flex-flow: row wrap
  align-items: flex-end
  justify-content: space-between
  &__header
    display: flex
    align-items: center
    justify-content: center
    width: 100%
.tag-select
  flex: 1 1 0
.sort-select
  flex: 0 1 27%
  margin-left: $base-padding*2

.loading-icon
  display: flex
  align-items: center
  justify-content: center
  padding-top: 10rem
  padding-bottom: 10rem

.patch-catalog
  display: flex;
  flex-flow: row wrap
  align-items: flex-start
  justify-content: flex-start
  @media screen and (max-width: 48rem)
    margin-right: $base-padding * -1
    margin-left: $base-padding * -1

.tag-select
  &__selected-tag
    position: relative
    display: flex
    flex-flow: row nowrap
    align-items: center
    justify-content: space-between
    margin-right: $base-padding
    padding: $base-padding*.25 $base-padding
    font-size: .9rem
    text-transform: capitalize
    background-color: $accent--light
    cursor: pointer
    &::before
      content: ''
      position: absolute
      top: 0
      left: 0
      height: 100%
      width: 100%
      z-index: 1
      background-color: $black
      opacity: 0
      transition: $base-transition
      cursor: pointer
    &:hover::before
      opacity: .12
  &__selected-tag-remove
    margin-left: $base-padding

.patch-listing
  position: relative
  flex-basis: 50%
  flex-shrink: 1
  @media screen and (min-width: 36rem)
    flex-basis: (100%/3)
  @media screen and (min-width: 48rem)
    flex-basis: 25%
  @media screen and (min-width: 60rem)
    flex-basis: (100%/6)
  @media screen and (min-width: 100rem)
    flex-basis: (100%/8)
  &::before
    content: ''
    position: absolute
    top: 0
    left: 0
    height: 100%
    width: 100%
    z-index: 1
    background-color: $white
    opacity: 0
    transition: $base-transition
    cursor: pointer
  &:hover::before
    opacity: .24

  &__image
    width: 100%

  &__placeholder
    display: flex
    align-items: center
    justify-content: center
    height: 100%
    width: 100%


.load-more
  display: flex
  flex-flow: column nowrap
  align-items: center
  padding-top: $base-padding

  &__page-details
    font-size: $font-size--small
    color: $accent--dark

  &__progress
    width: 25%
    margin: 0

  &__load-more-button
    width: 25%
    @media screen and (max-width: 48rem)
      width: 100%

  &__go-to-top
    font-size: $font-size--small
    color: $accent--dark


</style>
